import { api } from '../services/api';
import React, { useState, useEffect, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { detectPosition, getPlayerStyle } from '../utils/playerUtils';

const PositionStats = ({ token, position }) => {
  const [allPlayers, setAllPlayers] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadPlayers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getPlayers(token);
      
      const playersWithPositions = data.map(p => ({
        ...p,
        autoPosition: detectPosition(p),
        playerStyle: getPlayerStyle(p)
      }));
      const filtered = playersWithPositions.filter(p => p.autoPosition === position);
      setAllPlayers(filtered);
    } catch (error) {
      console.error('Error loading players:', error);
    } finally {
      setLoading(false);
    }
  }, [token, position]);

  useEffect(() => {
    loadPlayers();
  }, [loadPlayers]);

  // Safe Tailwind Configuration for each Position
  const getPositionConfig = () => {
    const configs = {
      'Goalkeeper': {
        icon: 'front_hand',
        themeText: 'text-purple-400',
        themeBg: 'bg-purple-500/10',
        themeBorder: 'border-purple-500/20',
        gradient: 'from-purple-500 to-pink-500',
        chartHex: '#a855f7',
        stats: [
          { key: 'clean_sheets', label: 'Clean Sheets', colorClass: 'text-emerald-400' },
          { key: 'total_saves', label: 'Saves', colorClass: 'text-purple-400' },
          { key: 'total_shots_faced', label: 'Shots Faced', colorClass: 'text-amber-400' },
          { key: 'total_penalties_saved', label: 'Penalty Saves', colorClass: 'text-rose-400' }
        ],
        chartKey: 'clean_sheets',
        chartLabel: 'Clean Sheets'
      },
      'Forward': {
        icon: 'sports_score',
        themeText: 'text-rose-400',
        themeBg: 'bg-rose-500/10',
        themeBorder: 'border-rose-500/20',
        gradient: 'from-rose-500 to-orange-500',
        chartHex: '#fb7185',
        stats: [
          { key: 'total_goals', label: 'Goals', colorClass: 'text-rose-400' },
          { key: 'total_assists', label: 'Assists', colorClass: 'text-emerald-400' },
          { key: 'matches_played', label: 'Matches', colorClass: 'text-stone-300' }
        ],
        chartKey: 'total_goals',
        chartLabel: 'Goals Scored'
      },
      'Midfielder': {
        icon: 'handshake',
        themeText: 'text-emerald-400',
        themeBg: 'bg-emerald-500/10',
        themeBorder: 'border-emerald-500/20',
        gradient: 'from-emerald-500 to-teal-500',
        chartHex: '#34d399',
        stats: [
          { key: 'total_assists', label: 'Assists', colorClass: 'text-emerald-400' },
          { key: 'total_goals', label: 'Goals', colorClass: 'text-rose-400' },
          { key: 'matches_played', label: 'Matches', colorClass: 'text-stone-300' }
        ],
        chartKey: 'total_assists',
        chartLabel: 'Assists (Playmaking)'
      },
      'Defender': {
        icon: 'shield',
        themeText: 'text-blue-400',
        themeBg: 'bg-blue-500/10',
        themeBorder: 'border-blue-500/20',
        gradient: 'from-blue-500 to-cyan-500',
        chartHex: '#60a5fa',
        stats: [
          { key: 'matches_played', label: 'Matches', colorClass: 'text-stone-300' },
          { key: 'total_assists', label: 'Assists', colorClass: 'text-emerald-400' },
          { key: 'total_goals', label: 'Goals', colorClass: 'text-rose-400' }
        ],
        chartKey: 'matches_played',
        chartLabel: 'Matches Played'
      }
    };
    return configs[position] || configs['Forward'];
  };

  const config = getPositionConfig();

  const getTopPlayers = () => {
    return [...allPlayers]
      .sort((a, b) => (b[config.chartKey] || 0) - (a[config.chartKey] || 0))
      .slice(0, 5);
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-surface-container-highest border border-outline-variant/30 rounded-xl p-4 shadow-2xl backdrop-blur-md">
          <p className="text-on-background font-lexend font-bold mb-1">{payload[0].payload.name}</p>
          <p className={`${config.themeText} font-manrope font-semibold`}>{payload[0].value} {payload[0].name}</p>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-[calc(100vh-200px)]">
        <div className={`animate-spin rounded-full h-16 w-16 border-b-2 ${config.themeBorder.replace('border-', 'border-').replace('/20', '')}`}></div>
      </div>
    );
  }

  if (allPlayers.length === 0) {
    return (
      <div className="bg-surface-container border border-outline-variant/20 rounded-3xl p-16 text-center shadow-lg animate-fadeIn pb-32">
        <div className="w-20 h-20 bg-background rounded-full flex items-center justify-center mx-auto mb-6 border border-outline-variant/30 shadow-inner">
          <span className="material-symbols-outlined text-4xl text-stone-600">{config.icon}</span>
        </div>
        <h3 className="text-3xl sm:text-4xl font-black text-on-background font-lexend mb-3">No {position}s Detected</h3>
        <p className="text-stone-400 font-manrope max-w-md mx-auto text-lg">
          Operators are dynamically classified based on their telemetry. Ensure accurate match logging to populate this matrix.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6 sm:space-y-8 animate-fadeIn pb-24">
      
      {/* AI Context Banner */}
      <div className={`${config.themeBg} border ${config.themeBorder} rounded-2xl p-5 flex items-start gap-4`}>
        <span className={`material-symbols-outlined ${config.themeText} mt-0.5`}>smart_toy</span>
        <div>
          <h4 className={`${config.themeText} font-lexend font-bold text-sm mb-1`}>Algorithmic Classification</h4>
          <p className="text-stone-400 font-manrope text-sm leading-relaxed">
            Players are classified as {position}s based on performance ratios.
            {position === 'Midfielder' && ' High assist creation or balanced goal/assist dynamics.'}
            {position === 'Forward' && ' High volume goal-scoring and attacking metrics.'}
            {position === 'Goalkeeper' && ' Defensive parameters including saves and shots faced.'}
            {position === 'Defender' && ' Defensive presence and consistent appearances.'}
          </p>
        </div>
      </div>

      {/* Header Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        
        {/* Total Position Count Card */}
        <div className="bg-surface-container border border-outline-variant/20 rounded-3xl p-6 relative overflow-hidden group hover:-translate-y-1 hover:bg-surface-container-high transition-all duration-300 shadow-lg">
          <div className="absolute -top-4 -right-4 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
            <span className="material-symbols-outlined text-[100px] text-stone-400">{config.icon}</span>
          </div>
          <div className="relative z-10 flex flex-col justify-between h-full">
            <div className="flex items-center justify-between mb-4">
              <p className="text-stone-400 font-manrope text-xs font-bold uppercase tracking-widest">Active {position}s</p>
              <div className={`w-10 h-10 ${config.themeBg} rounded-xl flex items-center justify-center border ${config.themeBorder}`}>
                <span className={`material-symbols-outlined ${config.themeText}`}>{config.icon}</span>
              </div>
            </div>
            <h3 className="text-4xl sm:text-5xl font-lexend font-black text-on-background tracking-tight">{allPlayers.length}</h3>
          </div>
        </div>

        {/* Dynamic Stat Cards */}
        {config.stats.slice(0, 3).map((stat, idx) => (
          <div key={idx} className="bg-surface-container border border-outline-variant/20 rounded-3xl p-6 relative overflow-hidden group hover:-translate-y-1 hover:bg-surface-container-high transition-all duration-300 shadow-lg">
            <div className="relative z-10 flex flex-col justify-between h-full">
              <div className="flex items-center justify-between mb-4">
                <p className="text-stone-400 font-manrope text-xs font-bold uppercase tracking-widest">{stat.label}</p>
                <div className={`w-10 h-10 bg-background rounded-xl flex items-center justify-center border border-outline-variant/30`}>
                  <span className={`material-symbols-outlined ${stat.colorClass} text-lg`}>monitoring</span>
                </div>
              </div>
              <h3 className="text-4xl sm:text-5xl font-lexend font-black text-on-background tracking-tight">
                {allPlayers.reduce((sum, p) => sum + (p[stat.key] || 0), 0)}
              </h3>
            </div>
          </div>
        ))}
      </div>

      {/* Leaderboard Chart */}
      <div className="bg-surface-container border border-outline-variant/20 rounded-3xl p-6 sm:p-8 shadow-lg">
        <div className="mb-8">
          <h2 className="text-on-background text-2xl font-lexend font-bold mb-1">{config.chartLabel} Leaders</h2>
          <p className="text-stone-400 text-sm font-manrope">Top performing {position.toLowerCase()}s by {config.chartLabel.toLowerCase()}</p>
        </div>
        
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={getTopPlayers()} margin={{ top: 20, right: 0, left: -20, bottom: 20 }}>
            <defs>
              <linearGradient id="posGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={config.chartHex} stopOpacity={1}/>
                <stop offset="100%" stopColor={config.chartHex} stopOpacity={0.4}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" vertical={false} />
            <XAxis 
              dataKey="name" 
              tick={{ fontSize: 12, fill: '#a8a29e', fontFamily: 'Manrope' }}
              axisLine={false}
              tickLine={false}
              tickMargin={10}
            />
            <YAxis tick={{ fontSize: 12, fill: '#a8a29e', fontFamily: 'Manrope' }} axisLine={false} tickLine={false} />
            <Tooltip content={<CustomTooltip />} cursor={{fill: '#1a1a1a'}} />
            <Bar 
              dataKey={config.chartKey} 
              fill="url(#posGradient)" 
              name={config.chartLabel} 
              radius={[8, 8, 0, 0]}
              barSize={50}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Players List Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
        {allPlayers.map((player, index) => (
          <div
            key={player.id}
            className="bg-surface-container border border-outline-variant/20 rounded-3xl p-6 shadow-lg hover:bg-surface-container-high transition-all group"
          >
            {/* Header */}
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-center gap-4">
                <div className="relative">
                  <div className={`w-14 h-14 bg-gradient-to-br ${config.gradient} rounded-2xl flex items-center justify-center shadow-lg`}>
                    <span className="material-symbols-outlined text-white text-2xl" style={{fontVariationSettings: "'FILL' 1"}}>{config.icon}</span>
                  </div>
                  {index < 3 && (
                    <div className="absolute -bottom-2 -right-2 w-7 h-7 bg-background rounded-xl flex items-center justify-center border-2 border-surface-container">
                      <span className={`${config.themeText} text-xs font-black font-lexend`}>{index + 1}</span>
                    </div>
                  )}
                </div>
                <div>
                  <h3 className="text-on-background font-lexend font-bold text-xl">{player.name}</h3>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`text-[9px] px-2.5 py-1 rounded-lg font-bold font-manrope uppercase tracking-wider ${config.themeBg} ${config.themeText} border ${config.themeBorder}`}>
                      {position}
                    </span>
                    <span className="text-[9px] px-2.5 py-1 rounded-lg font-bold font-manrope uppercase tracking-wider bg-background border border-outline-variant/50 text-stone-300">
                      {player.playerStyle}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 bg-background rounded-2xl p-4 border border-outline-variant/30">
              {config.stats.map((stat, idx) => (
                <div key={idx} className="text-center p-2">
                  <p className={`${stat.colorClass} font-lexend font-black text-xl mb-1 leading-none`}>
                    {player[stat.key] || 0}
                  </p>
                  <p className="text-stone-500 font-manrope text-[9px] font-bold uppercase tracking-widest">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PositionStats;
